import { Box, Typography, CircularProgress, Paper, Chip, Divider } from "@mui/material";
import React, { useEffect } from "react";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import { useDispatch, useSelector } from "react-redux";
import { getOrderHistory } from "../../../Redux/Customers/Order/Action";
import { useNavigate } from "react-router-dom";
import OrderCard from "./OrderCard";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PendingIcon from "@mui/icons-material/Pending";
import InventoryIcon from "@mui/icons-material/Inventory";
import CancelIcon from "@mui/icons-material/Cancel";
import NoteAltIcon from "@mui/icons-material/NoteAlt";


/**
 * Status configuration for order display
 */
const STATUS_CONFIG = {
  PENDING: { color: "warning", icon: PendingIcon, label: "Pending" },
  PLACED: { color: "info", icon: InventoryIcon, label: "Placed" },
  CONFIRMED: { color: "info", icon: CheckCircleIcon, label: "Confirmed" },
  SHIPPED: { color: "primary", icon: LocalShippingIcon, label: "Shipped" },
  DELIVERED: { color: "success", icon: CheckCircleIcon, label: "Delivered" },
  CANCELLED: { color: "error", icon: CancelIcon, label: "Cancelled" },
  RETURNED: { color: "default", icon: CancelIcon, label: "Returned" },
};

const Order = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const jwt = localStorage.getItem("jwt");
  const { order } = useSelector((store) => store);

  useEffect(() => {
    if (jwt) {
      dispatch(getOrderHistory({ jwt }));
    }
  }, [jwt, dispatch]);

  // Format date: Jan 27, 2026
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Empty State Component
  const EmptyState = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 10,
        px: 3,
      }}
    >
      <Box
        sx={{
          width: 120,
          height: 120,
          borderRadius: "50%",
          bgcolor: "grey.100",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 3,
        }}
      >
        <ShoppingBagOutlinedIcon sx={{ fontSize: 60, color: "grey.400" }} />
      </Box>
      <Typography variant="h5" fontWeight="600" gutterBottom>
        No Orders Yet
      </Typography>
      <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 3, maxWidth: 400 }}>
        Looks like you haven't placed any orders yet. Start shopping to see your orders here!
      </Typography>
      <Box
        component="button"
        onClick={() => navigate("/")}
        sx={{
          px: 4,
          py: 1.5,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          border: "none",
          borderRadius: 2,
          fontSize: "1rem",
          fontWeight: 600,
          cursor: "pointer",
          "&:hover": {
            background: "linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)",
          },
        }}
      >
        Start Shopping
      </Box>
    </Box>
  );

  if (order.loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="px-5 lg:px-20 py-10">
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          My Orders
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your orders and track their status
        </Typography>
      </Box>

      {(!order.orders || order.orders.length === 0) ? (
        <EmptyState />
      ) : (
        <Box className="space-y-6">
          {order.orders.map((orderItem) => {
            const statusCfg = STATUS_CONFIG[orderItem.orderStatus] || STATUS_CONFIG.PENDING;
            const StatusIcon = statusCfg.icon;

            return (
              <Paper
                key={orderItem.id}
                elevation={1}
                sx={{
                  borderRadius: 3,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: "grey.200",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    boxShadow: 4,
                    cursor: "pointer",
                  },
                }}
                onClick={() => navigate(`/account/order/${orderItem.id}`)}
              >
                {/* Order Header */}
                <Box
                  sx={{
                    bgcolor: "grey.50",
                    px: 3,
                    py: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  <Box className="flex gap-4 items-center">
                    <Typography variant="subtitle1" fontWeight="bold">
                      Order #{orderItem.id}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(orderItem.orderDate || orderItem.createdAt)}
                    </Typography>
                    {orderItem.specialInstructions && (
                      <Chip
                        icon={<NoteAltIcon sx={{ fontSize: "0.9rem !important" }} />}
                        label="With Instructions"
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: "0.7rem", height: "20px" }}
                      />
                    )}
                  </Box>


                  <Chip
                    icon={<StatusIcon sx={{ fontSize: "1rem !important" }} />}
                    label={statusCfg.label}
                    color={statusCfg.color}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>

                <Divider />

                {/* Order Items */}
                <Box sx={{ px: 3, py: 1 }}>
                  {orderItem.orderItems?.map((item, index) => (
                    <Box key={item.id}>
                      <OrderCard item={item} />
                      {index < orderItem.orderItems.length - 1 && (
                        <Divider sx={{ borderStyle: "dashed", my: 1 }} />
                      )}
                    </Box>
                  ))}
                </Box>

                <Divider />

                {/* Order Footer */}
                <Box
                  sx={{
                    px: 3,
                    py: 2,
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    bgcolor: "white",
                  }}
                >
                  <Typography variant="body1" fontWeight="bold">
                    Total Amount:{" "}
                    <Box component="span" color="primary.main">
                      ${orderItem.totalDiscountedPrice?.toFixed(2)}
                    </Box>
                  </Typography>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default Order;
